import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Row, Col, Steps, Button, StepProps, Modal, Space, message, Form } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import RegistrationWidget from 'src/components/dashboard/common-widget/registration-widget/registration-widget';

import { PAGE_ROUTE } from 'src/constants/route';
import { scrollPageToTop, scrollUploadFormIfError } from 'src/helpers/window';
import useStore from 'src/hooks/useStore';
import { IMortgageSupportingDoc } from 'src/interfaces/mortgage-deal/supporting-document';

import { i18nKey } from 'src/locales/i18n';
import OnBoardingRegisterPage from 'src/pages/protected/onboarding/onboarding';
import { IMortgageDealDetails } from 'src/store/mortgage-deal/mortgage-deal-details.store';
import EFormMortgageDeal from '../e-form/e-form-mortgage-deal';

import BorrowerRequestMortgageDeal from './components/borrower-request-mortgage-deal/individual/borrower-request-mortgage-deal';
import InviteGuarantor from './components/invite-guarantor/invite-guarantor';
import MortgageDealDetails from './components/mortgage-deal-details/mortgage-deal-details';
import MortgageDealSupportingDoc from './components/mortgage-deal-supporting-doc/mortgage-deal-supporting-document';
import styles from './mortgage-deal-form.module.less';

const MortgageDealForm = () => {
	const [t] = useTranslation();

	const navigator = useNavigate();

	const { state } = useLocation();

	const [step, setStep] = useState(0);

	const [isOpenConfirmBRForm, setOpenConfirmBRForm] = useState(false);

	const [openEForm, setOpenEForm] = useState(false);

	const [docForm] = Form.useForm();

	const [inviteForm] = Form.useForm();

	const mortgageDealDetailsStore: IMortgageDealDetails = useStore('mortgageDealDetails');

	const [searchParams] = useSearchParams();

	const mortgageId = searchParams.get('id');

	const toSubmitForm = searchParams.get('toSubmitForm');

	const toInviteGuarantor = searchParams.get('toInviteGuarantor');

	const onCancel = () => {
		setOpenConfirmBRForm(false);
	};

	const onConfirmBRForm = () => {
		setStep(step + 1);
		setOpenConfirmBRForm(false);

		message.success(t(i18nKey.mortgageDeal.requestForm.message.confirmForm), 1);
	};

	const isSuccessForm = () => {
		const listDocumentNotRequire: string[] = [
			'othersDocument',
			'thirdPartyAgreementDocument',
			'energyPerformanceDocument'
		];
		const objSupportingDocuments = mortgageDealDetailsStore.supportingDocuments;

		if(Object.keys(objSupportingDocuments).length === 0){
			return false;
		}
		
		const keyOfDocumentRequire = Object.keys(objSupportingDocuments).filter((item) => {
			return !listDocumentNotRequire.includes(item);
		});

		return !keyOfDocumentRequire.some(
			(key) => (objSupportingDocuments[key as keyof IMortgageSupportingDoc] || []).length === 0
		);
	};

	const onSubmitClick = async () => {
		try {
			await docForm.validateFields();
			setOpenEForm(true);
		} catch {
			message.error(t(i18nKey.feedback.pleaseInputRequiredFiles));
			scrollUploadFormIfError(docForm);
		}
	};

	const onEFormClose = () => {
		setOpenEForm(false);
	};

	const onSaveClick = () => {
		switch (step) {
			case 0:
				setOpenConfirmBRForm(!isOpenConfirmBRForm);
				break;
			case 1:
				setStep(step + 1);
				message.success(t(i18nKey.mortgageDeal.requestForm.message.confirmForm), 1);
				break;
			case 2:
				setStep(step + 1);
				message.success(t(i18nKey.mortgageDeal.requestForm.message.inviteAGuarantor), 1);
				break;

			case 3:
				message.success(t(i18nKey.mortgageDeal.requestForm.supportingDocument.saveDraft));
				navigator(PAGE_ROUTE.DASHBOARD.concat(PAGE_ROUTE.DASHBOARD_MORTGAGE));
				break;
			default:
				break;
		}
	};

	const onBackClick = () => {
		setStep(step - 1);
	};

	const stepItems: StepProps[] = [
		{
			title: `${t(i18nKey.mortgageDeal.requestForm.steps.borrowerRequest)}`
		},
		{
			title: `${t(i18nKey.mortgageDeal.requestForm.steps.mortgageDealDetails)}`
		},
		{
			title: `${t(i18nKey.mortgageDeal.requestForm.steps.inviteGuarantor)}`
		},
		{
			title: `${t(i18nKey.mortgageDeal.requestForm.steps.supportingDocument)}`
		}
	];

	const renderStepPage = (step: number) => {
		switch (step) {
			case 0:
				return <BorrowerRequestMortgageDeal onConfirmForm={onSaveClick} />;
			case 1:
				return (
					<MortgageDealDetails
						onBack={onBackClick}
						onConfirmForm={onSaveClick}
						cloneMortgageId={state?.cloneMortgageId}
					/>
				);
			case 2:
				return <InviteGuarantor onBack={onBackClick} onConfirmForm={onSaveClick} inviteForm={inviteForm} />;
			case 3:
				return <MortgageDealSupportingDoc onBack={onBackClick} form={docForm} onSaveClick={onSaveClick} />;
			default:
				break;
		}
	};

	const onCancelRequestForm = () => {
		mortgageDealDetailsStore.dispose();

		navigator(PAGE_ROUTE.DASHBOARD.concat(PAGE_ROUTE.DASHBOARD_MORTGAGE));
	};

	useEffect(() => {
		scrollPageToTop();
	}, []);

	useEffect(() => {
		if (mortgageId !== null) {
			mortgageDealDetailsStore
				.getMortgageDealDetails(mortgageId)
				.then(() => {
					if (toSubmitForm) {
						setStep(3);
					}
					if (toInviteGuarantor) {
						setStep(2);
					}
				})
				.catch();
		} else {
			mortgageDealDetailsStore.dispose();
		}
	}, []);

	return (
		<>
			<OnBoardingRegisterPage>
				<RegistrationWidget title={t(i18nKey.mortgageDeal.requestForm.title)}>
					<div className={styles.subLevelForm}>
						<Steps
							current={step}
							labelPlacement="vertical"
							items={stepItems}
							responsive
							style={{ justifyContent: 'center' }}
						/>
					</div>
					<div className={styles.marginTop}>{renderStepPage(step)}</div>

					<Row gutter={[16, 16]} className={styles.marginTop}>
						<Col span={12}>
							<Button block type="primary" ghost onClick={onCancelRequestForm}>
								{t(i18nKey.common.cancel)}
							</Button>
						</Col>
						<Col span={12}>
							<Button block type="primary" disabled={step < 1 || !isSuccessForm()} onClick={onSubmitClick}>
								{t(i18nKey.common.submit)}
							</Button>
						</Col>
					</Row>
				</RegistrationWidget>
			</OnBoardingRegisterPage>
			<Modal
				open={isOpenConfirmBRForm}
				destroyOnClose
				title={null}
				footer={null}
				width={726}
				onCancel={onCancel}
				style={{
					borderRadius: 11
				}}
				centered
				closeIcon={
					<CloseOutlined
						style={{
							fontSize: '21px',
							color: '#595959'
						}}
					/>
				}>
				<Space align="center">
					<ExclamationCircleOutlined style={{ fontSize: '24px', color: '#FAAD14' }} />
					<div className={styles.confirmContent}>
						<p className={styles.confirmTitle}>
							{t(i18nKey.mortgageDeal.requestForm.borrowerRequest.formConfirm.ConfirmBorrowerRequest)}
						</p>
					</div>
				</Space>
				<Row style={{ margin: '24px 0 30px 0' }}>
					<Col span={24}>{t(i18nKey.mortgageDeal.requestForm.borrowerRequest.formConfirm.description)}</Col>
				</Row>
				<Row gutter={20}>
					<Col span={12}>
						<Button
							style={{ width: '100%', backgroundColor: '#848484', color: '#fff', maxHeight: '57px' }}
							onClick={() => {
								navigator(PAGE_ROUTE.DASHBOARD.concat(PAGE_ROUTE.DASHBOARD_CHANGE_REQUEST));
							}}>
							{t(i18nKey.mortgageDeal.requestForm.borrowerRequest.formConfirm.CRButton)}
						</Button>
					</Col>
					<Col span={12} className={styles.buttonConfirm}>
						<Button onClick={onConfirmBRForm}>
							{t(i18nKey.mortgageDeal.requestForm.borrowerRequest.formConfirm.confirmButton)}
						</Button>
					</Col>
				</Row>
			</Modal>

			<EFormMortgageDeal open={openEForm} onClose={onEFormClose} />
		</>
	);
};

export default observer(MortgageDealForm);
